import React from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../../auth/authStore";
import { onboardingApi } from "../../api/onboardingApi";
import { notificationsApi } from "../../api/notificationsApi";
import { useNotifications } from "../../notifications/notificationsStore";
import { useToast } from "../../ui/toast/ToastProvider";
import { profileApi } from "../../api/profileApi";
import { sessionsApi } from "../../api/sessionsApi";
import { walletApi } from "../../api/walletApi";
import { request } from "../../api/http";
import { openExternalUrl } from "../../utils/safeUrl";
import { buildUnavailableSectionsNotice } from "../../utils/userFacingApiError";
import AppSidebarV2 from "../layout/AppSidebarV2";
import "./StaffTodayDashboardV3.css";

const CANCELED_STATUSES = new Set(["CANCELED", "CANCELLED", "DECLINED", "REFUNDED"]);
const TERMINAL_STATUSES = new Set([
  ...CANCELED_STATUSES,
  "CLOSED",
  "ENDED",
  "COMPLETED",
  "COMPLETE",
  "DONE",
  "FINISHED",
]);

function asObject(value) {
  return value && typeof value === "object" ? value : {};
}

function clampPercent(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, Math.round(n)));
}

function extractOnboardingFlags(raw) {
  const src = asObject(raw);
  const state = asObject(src.state);
  const dismissed = Boolean(src.dismissed ?? state.dismissed ?? false);
  const completed = Boolean(src.completed ?? state.completed ?? false);
  const shouldShowRaw = src.shouldShow ?? state.shouldShow;
  const shouldShow = typeof shouldShowRaw === "boolean" ? shouldShowRaw : !dismissed && !completed;
  return { dismissed, shouldShow };
}

function hasAnyEnabledNotificationSettings(raw) {
  const settings = asObject(raw);
  const values = Object.values(settings);
  return values.length > 0 ? values.some((value) => value === true) : false;
}

function extractItems(raw) {
  if (Array.isArray(raw)) return raw;
  const obj = asObject(raw);
  if (Array.isArray(obj.content)) return obj.content;
  if (Array.isArray(obj.items)) return obj.items;
  if (Array.isArray(obj.items?.content)) return obj.items.content;
  if (Array.isArray(obj.item?.items)) return obj.item.items;
  if (Array.isArray(obj.item?.content)) return obj.item.content;
  if (Array.isArray(obj.item)) return obj.item;
  if (Array.isArray(obj.data)) return obj.data;
  return [];
}

function extractTotal(raw, fallbackItems = [], fallback = 0) {
  const obj = asObject(raw);
  const candidates = [
    obj.totalElements,
    obj.total,
    obj.count,
    obj.item?.totalElements,
    obj.item?.total,
    obj.item?.count,
  ];
  for (const value of candidates) {
    const n = Number(value);
    if (Number.isFinite(n)) return Math.max(0, n);
  }
  return Math.max(0, fallbackItems.length || Number(fallback) || 0);
}

function parseDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

function addDays(date, amount) {
  const next = new Date(date);
  next.setDate(next.getDate() + amount);
  return next;
}

function dayKey(value) {
  const date = value instanceof Date ? value : parseDate(value);
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function formatInt(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return "0";
  return new Intl.NumberFormat("ru-RU").format(number);
}

function formatMoney(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return "0 ₽";
  return `${new Intl.NumberFormat("ru-RU").format(Math.max(0, Math.round(number)))} ₽`;
}

function formatDate(value, options) {
  const date = value instanceof Date ? value : parseDate(value);
  if (!date) return "—";
  return new Intl.DateTimeFormat("ru-RU", options).format(date);
}

function formatTime(value) {
  return formatDate(value, { hour: "2-digit", minute: "2-digit" });
}

function firstName(value) {
  return String(value || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)[0] || "коллега";
}

function initials(value) {
  const parts = String(value || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2);
  if (!parts.length) return "БД";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
}

function normalizeRole(role) {
  return String(role || "")
    .replace(/^ROLE_/, "")
    .trim()
    .toUpperCase();
}

function rolePlanLabel(role) {
  const token = normalizeRole(role);
  if (token === "ADMIN" || token === "SUPER_ADMIN") return "Администратор";
  if (token === "MODERATOR") return "Модератор";
  return "Психолог";
}

function readStatus(raw) {
  const src = asObject(raw);
  const nested = asObject(src.sessionRoom || src.session_room || src.room || src.booking || src.appointment || src.session || src.conference);
  const values = [
    src.status,
    src.state,
    src.bookingStatus,
    src.booking_status,
    src.roomStatus,
    src.room_status,
    src.sessionStatus,
    src.session_status,
    nested.status,
    nested.state,
    nested.bookingStatus,
    nested.booking_status,
    nested.roomStatus,
    nested.room_status,
    nested.sessionStatus,
    nested.session_status,
  ];
  return values.map((item) => String(item || "").trim().toUpperCase()).find(Boolean) || "";
}

function resolveEventKind(raw, fallback = "INDIVIDUAL") {
  const src = asObject(raw);
  const token = String(
    src.kind ??
    src.type ??
    src.bookingType ??
    src.booking_type ??
    src.sessionType ??
    src.session_type ??
    ""
  )
    .trim()
    .toUpperCase();

  if (token === "GROUP") return "GROUP";
  if (token === "DIRECT" || token === "INDIVIDUAL") return "INDIVIDUAL";
  if (src.groupSessionId != null || src.group_session_id != null) return "GROUP";
  return fallback;
}

function eventIdentityKey(event = {}) {
  const kind = String(event.kind || "INDIVIDUAL").toUpperCase();
  const startAt = String(event.startAt || "");
  const primaryId = event.sourceGroupSessionId ?? event.id ?? "";
  return `${kind}:${primaryId}:${startAt}`;
}

function eventRichnessScore(event = {}) {
  let score = 0;
  if (event.url) score += 4;
  if (event.clientUserId != null) score += 2;
  if (event.title) score += 2;
  if (event.amount > 0) score += 1;
  if (event.source === "groups") score += 1;
  return score;
}

function mergeEvents(...collections) {
  const map = new Map();

  collections.flat().forEach((event) => {
    if (!event) return;
    const key = eventIdentityKey(event);
    const previous = map.get(key);
    if (!previous || eventRichnessScore(event) >= eventRichnessScore(previous)) {
      map.set(key, event);
    }
  });

  return Array.from(map.values());
}

function mapEvent(item = {}, kind = "INDIVIDUAL") {
  const raw = asObject(item);
  const resolvedKind = resolveEventKind(raw, kind);
  const nestedClient = asObject(raw.client || raw.user || raw.patient || raw.customer);
  const startAt =
    raw.startDateTime ||
    raw.start_datetime ||
    raw.startAt ||
    raw.startsAt ||
    raw.dateTime ||
    raw.date ||
    null;
  const title =
    raw.clientName ||
    raw.clientFullName ||
    nestedClient.fullName ||
    nestedClient.name ||
    raw.title ||
    (resolvedKind === "GROUP" ? "Групповая сессия" : "Индивидуальная сессия");

  return {
    id: raw.id ?? raw.bookingId ?? raw.groupSessionId ?? `${resolvedKind}-${startAt || Math.random()}`,
    kind: resolvedKind,
    title,
    startAt,
    clientUserId: raw.clientUserId ?? raw.clientId ?? nestedClient.id ?? null,
    status: readStatus(raw),
    url: raw.telemostUrl || raw.videoRoomUrl || raw.videoUrl || raw.roomUrl || raw.meetingUrl || "",
    format: resolvedKind === "GROUP" ? "Онлайн · группа" : "Онлайн · индивидуальная",
    amount: Number(raw.amount ?? raw.price ?? raw.priceAtTime ?? raw.totalAmount ?? 0) || 0,
    sourceGroupSessionId: raw.groupSessionId ?? raw.group_session_id ?? (resolvedKind === "GROUP" ? raw.id ?? null : null),
    source: kind === "GROUP" ? "groups" : "bookings",
  };
}

function isProfileComplete(profile = {}) {
  const src = asObject(profile);
  const price = Number(
    src.pricePerSession ??
    src.price_per_session ??
    src.sessionPrice ??
    src.price ??
    0
  );
  const hasAbout = Boolean(String(src.headline || src.about || src.description || src.bio || "").trim());
  if (src.isComplete || src.complete || src.profileComplete) return true;
  return hasAbout && Number.isFinite(price) && price > 0;
}

function hasWorkingHoursConfigured(profile = {}) {
  const src = asObject(profile);
  const schedule = asObject(src.schedule);
  const availability = asObject(src.availability);
  const daySlots = [
    schedule.days,
    schedule.workingDays,
    schedule.weekdays,
    availability.days,
    availability.slots,
    availability.weekdays,
    src.workHours,
    src.workingHours,
  ];

  const hasStructuredSchedule = daySlots.some((value) => Array.isArray(value) && value.length > 0);
  const hasTimeBounds = Boolean(
    schedule.slotMinutes ||
    schedule.startAt ||
    schedule.endAt ||
    availability.startAt ||
    availability.endAt ||
    src.workStartAt ||
    src.workEndAt
  );

  return hasStructuredSchedule || hasTimeBounds;
}

function buildOnboardingFallback({
  profile,
  clientsTotal,
  hasClientNotes,
  hasPaymentsRecorded,
  hasPushNotifications,
  upcomingEvents,
}) {
  const profileComplete = isProfileComplete(profile);
  const workHoursConfigured = hasWorkingHoursConfigured(profile);
  const hasClients = Number(clientsTotal) > 0;
  const hasSessionCreated = Array.isArray(upcomingEvents) && upcomingEvents.length > 0;

  const steps = [
    {
      id: "profile_completed",
      title: "Заполнить профиль",
      description: "Добавьте специализацию, описание, формат работы и стоимость сессии — это увидят клиенты при записи.",
      ctaLabel: "Открыть профиль",
      href: "/dashboard/settings?tab=psychologist",
      completed: profileComplete,
      optional: false,
    },
    {
      id: "working_hours_set",
      title: "Настроить рабочие часы",
      description: "Укажите дни и время приёма — свободные слоты появятся в форме записи.",
      ctaLabel: "Настроить расписание",
      href: "/dashboard/calendar",
      completed: workHoursConfigured,
      optional: false,
    },
    {
      id: "first_client_added",
      title: "Добавить клиента",
      description: "Создайте карточку: контакты, встречи и заметки будут в одном месте.",
      ctaLabel: "Добавить клиента",
      href: "/dashboard/clients",
      completed: hasClients,
      optional: false,
    },
    {
      id: "first_session_created",
      title: "Запланировать первую сессию",
      description: "Создайте запись в календаре или отправьте клиенту ссылку для самозаписи.",
      ctaLabel: "Создать запись",
      href: "/dashboard/calendar",
      completed: hasSessionCreated,
      optional: false,
    },
    {
      id: "first_note_created",
      title: "Создать заметку",
      description: "После встречи зафиксируйте наблюдения — заметки видите только вы.",
      ctaLabel: "Открыть заметки",
      href: "/dashboard/notes",
      completed: Boolean(hasClientNotes),
      optional: false,
    },
    {
      id: "first_payment_recorded",
      title: "Отметить оплату",
      description: "Фиксируйте оплаты, чтобы видеть доход и неоплаченные встречи.",
      ctaLabel: "Открыть финансы",
      href: "/dashboard/subscription",
      completed: Boolean(hasPaymentsRecorded),
      optional: true,
    },
    {
      id: "notifications_enabled",
      title: "Включить уведомления",
      description: "Напоминания о сессиях, сообщениях и важных событиях.",
      ctaLabel: "Открыть уведомления",
      href: "/notifications",
      completed: Boolean(hasPushNotifications),
      optional: true,
    },
  ];
  const countedSteps = steps.filter((step) => !step.optional);
  const completedCount = countedSteps.filter((step) => step.completed).length;
  const totalCount = countedSteps.length;
  return {
    completedCount,
    totalCount,
    progressPercent: clampPercent(totalCount ? (completedCount / totalCount) * 100 : 0),
    shouldShow: completedCount < totalCount,
    dismissed: false,
    nextStepId: steps.find((step) => !step.completed && !step.optional)?.id || steps.find((step) => !step.completed)?.id || null,
    steps,
  };
}

function normalizeOnboardingPayload(raw, fallback) {
  const src = asObject(raw);
  const rawSteps = Array.isArray(src.steps) ? src.steps.filter(Boolean) : [];
  const fallbackSteps = Array.isArray(fallback.steps) ? fallback.steps : [];
  const fallbackById = new Map(fallbackSteps.map((step) => [String(step.id || ""), step]));
  const apiById = new Map(rawSteps.map((step) => [String(step?.id || ""), step]));
  const fallbackIds = new Set(fallbackSteps.map((step) => String(step.id || "")));

  const steps = [
    ...fallbackSteps.map((step) => {
      const merged = { ...step, ...asObject(apiById.get(String(step.id || ""))) };
      if (merged.id === "profile_completed") {
        return { ...merged, href: "/dashboard/settings?tab=psychologist", ctaLabel: "Открыть профиль" };
      }
      if (merged.id === "working_hours_set") {
        return { ...merged, href: "/dashboard/calendar", ctaLabel: merged.ctaLabel || "Настроить расписание" };
      }
      if (merged.id === "first_session_created") {
        return { ...merged, href: "/dashboard/calendar", ctaLabel: merged.ctaLabel || "Создать запись" };
      }
      if (merged.id === "first_note_created") {
        return { ...merged, href: "/dashboard/notes", ctaLabel: merged.ctaLabel || "Открыть заметки" };
      }
      if (merged.id === "first_payment_recorded") {
        return { ...merged, href: "/dashboard/subscription", ctaLabel: merged.ctaLabel || "Открыть финансы" };
      }
      if (merged.id === "notifications_enabled") {
        return { ...merged, href: "/notifications", ctaLabel: merged.ctaLabel || "Открыть уведомления" };
      }
      return merged;
    }),
      ...rawSteps
      .filter((step) => step?.id && !fallbackIds.has(String(step.id)))
      .map((step) => ({ ...asObject(step) })),
  ].map((step) => {
    const base = fallbackById.get(String(step.id || "")) || {};
    return {
      ...base,
      ...step,
      completed: Boolean(step.completed || base.completed),
      optional: Boolean(step.optional ?? base.optional),
    };
  });

  const countedSteps = steps.filter((step) => !step.optional);
  const fallbackCompleted = countedSteps.filter((step) => step.completed).length;
  const fallbackTotal = countedSteps.length;
  const completedCount = Number.isFinite(Number(src.completedCount)) ? Number(src.completedCount) : fallbackCompleted;
  const totalCount = Number.isFinite(Number(src.totalCount)) ? Number(src.totalCount) : fallbackTotal;
  const progressPercent = Number.isFinite(Number(src.progressPercent))
    ? clampPercent(src.progressPercent)
    : clampPercent(totalCount ? (completedCount / totalCount) * 100 : 0);
  const dismissed = Boolean(src.dismissed ?? fallback.dismissed);
  const shouldShowRaw = src.shouldShow;
  const shouldShow = typeof shouldShowRaw === "boolean"
    ? shouldShowRaw
    : !dismissed && completedCount < totalCount;
  const nextStepId = String(
    src.nextStepId ||
    steps.find((step) => !step.completed && !step.optional)?.id ||
    steps.find((step) => !step.completed)?.id ||
    ""
  ).trim() || null;

  return {
    completedCount,
    totalCount,
    progressPercent,
    dismissed,
    shouldShow,
    nextStepId,
    steps,
  };
}

function ProgressRing({ pct }) {
  const radius = 17;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (pct / 100) * circumference;

  return (
    <div className="today-v3__ring">
      <svg width="42" height="42" viewBox="0 0 42 42" aria-hidden="true">
        <circle cx="21" cy="21" r={radius} fill="none" stroke="currentColor" strokeWidth="4" className="today-v3__ring-track" />
        <circle
          cx="21"
          cy="21"
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth="4"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform="rotate(-90 21 21)"
          className="today-v3__ring-fill"
        />
      </svg>
      <strong>{pct}%</strong>
    </div>
  );
}

function IconCalendar({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M16 3v4" />
      <path d="M8 3v4" />
      <path d="M3 10h18" />
    </svg>
  );
}

function IconUser({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="7" r="4" />
      <path d="M1 20c0-4 3.6-7 8-7s8 3 8 7" />
      <path d="M17 11a4 4 0 1 1 0-8 4 4 0 0 1 0 8Z" opacity="0.55" />
    </svg>
  );
}

function IconNote({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 3h7l5 5v13H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" />
      <path d="M14 3v5h5" />
    </svg>
  );
}

function IconLink({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  );
}

function IconPlus({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </svg>
  );
}

function IconWallet({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 7.5A2.5 2.5 0 0 1 6.5 5H19a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H6.5A2.5 2.5 0 0 1 4 16.5v-9Z" />
      <path d="M4 8h15" />
      <circle cx="16" cy="13" r="1.5" />
    </svg>
  );
}

function payoutStatusLabel(status) {
  const token = String(status || "").trim().toUpperCase();
  if (token === "PAID") return "Выплачено";
  if (token === "APPROVED") return "Подтверждено";
  if (token === "REJECTED") return "Отклонено";
  if (token === "CANCELLED") return "Отменено";
  if (token === "PENDING") return "На проверке";
  return "Обновляется";
}

function toErrorSnapshot(error) {
  if (!error) return null;
  return {
    status: Number(error?.status || 0) || 0,
    code: String(error?.code || error?.error || "").trim(),
    message: String(error?.message || "").trim(),
  };
}

function hasDisabledMarker(error, marker) {
  const needle = String(marker || "").trim().toUpperCase();
  if (!needle) return false;
  const status = Number(error?.status || 0) || 0;
  const haystack = `${error?.code || ""} ${error?.message || ""}`.toUpperCase();
  return (status === 404 || status === 503) && haystack.includes(needle);
}

function OnboardingCard({
  dismissed,
  expanded,
  doneCount,
  requiredDoneCount,
  requiredTotalCount,
  totalCount,
  progressPercent,
  steps,
  currentStep,
  dismissing = false,
  onDismiss,
  onToggleExpanded,
}) {
  if (dismissed) return null;

  return (
    <section className="today-v3__panel today-v3__onboarding">
      <div className="today-v3__onboarding-head">
        <div>
          <h2>Настройка кабинета</h2>
          <p>Подсказки помогут быстро настроить кабинет без поиска нужных разделов.</p>
        </div>

        <div className="today-v3__onboarding-progress">
          <ProgressRing pct={progressPercent} />
        <div className="today-v3__onboarding-progress-copy">
          <strong>{doneCount} из {totalCount}</strong>
          <span>
            шагов готово
            {requiredTotalCount > 0 && requiredTotalCount !== totalCount
              ? ` · обязательных ${requiredDoneCount} из ${requiredTotalCount}`
              : ""}
          </span>
        </div>
          <button type="button" className="today-v3__text-btn" onClick={onDismiss} disabled={dismissing}>
            {dismissing ? "Скрываем..." : "Скрыть"}
          </button>
        </div>
      </div>

      <div className="today-v3__onboarding-current">
        <div className="today-v3__onboarding-current-main">
          <div className="today-v3__step-num">{currentStep.number}</div>
          <div>
            <strong>Следующий шаг: {currentStep.title.toLowerCase()}</strong>
            <span>{currentStep.desc}</span>
          </div>
        </div>

        <button type="button" className="today-v3__primary-btn" onClick={currentStep.onClick}>
          {currentStep.cta}
        </button>
      </div>

      <button type="button" className="today-v3__expand" onClick={onToggleExpanded}>
        <span>{expanded ? "Скрыть шаги" : "Показать все шаги"}</span>
        <span className={expanded ? "is-open" : ""}>›</span>
      </button>

      {expanded ? (
        <div className="today-v3__steps">
          {steps.map((step) => (
            <button key={step.id} type="button" className={`today-v3__step ${step.done ? "is-done" : ""} ${step.id === currentStep.id ? "is-current" : ""}`} onClick={step.onClick}>
              <span className="today-v3__step-check">{step.done ? "✓" : step.number}</span>
              <span className="today-v3__step-body">
                <span className="today-v3__step-topline">
                  <span className="today-v3__step-title">{step.title}</span>
                  <span className={`today-v3__step-state ${step.done ? "is-done" : step.id === currentStep.id ? "is-current" : ""}`}>
                    {step.done ? "Готово" : step.id === currentStep.id ? "Сейчас" : step.optional ? "Доп." : `Шаг ${step.number}`}
                  </span>
                </span>
                <span className="today-v3__step-desc">{step.desc}</span>
              </span>
            </button>
          ))}
        </div>
      ) : null}
    </section>
  );
}

function QuickActionsCard({ isNew, onOpenAppointments, onOpenClients, onCopyLink, onOpenCalendar }) {
  const items = isNew
    ? [
        { label: "Создать запись", icon: <IconPlus size={16} />, onClick: onOpenAppointments },
        { label: "Добавить клиента", icon: <IconUser size={15} />, onClick: onOpenClients },
        { label: "Скопировать ссылку для записи", icon: <IconLink size={15} />, onClick: onCopyLink },
        { label: "Настроить рабочие часы", icon: <IconCalendar size={15} />, onClick: onOpenCalendar },
      ]
    : [
        { label: "Создать запись", icon: <IconPlus size={16} />, onClick: onOpenAppointments },
        { label: "Добавить клиента", icon: <IconUser size={15} />, onClick: onOpenClients },
        { label: "Скопировать ссылку", icon: <IconLink size={15} />, onClick: onCopyLink },
        { label: "Настроить расписание", icon: <IconCalendar size={15} />, onClick: onOpenCalendar },
      ];

  return (
    <aside className="today-v3__panel today-v3__quick-card">
      <h2 className="today-v3__section-title">Быстрые действия</h2>
      <div className="today-v3__quick-list">
        {items.map((item) => (
          <button key={item.label} type="button" className="today-v3__quick-item" onClick={item.onClick}>
            <span className="today-v3__quick-icon">{item.icon}</span>
            <span>{item.label}</span>
          </button>
        ))}
      </div>
    </aside>
  );
}

function HeroCard({
  hasSession,
  nextEvent,
  isNew,
  hasScheduleError,
  onOpenAppointments,
  onOpenClients,
  onCopyLink,
  onOpenClientCard,
  onOpenNotes,
  onOpenEvent,
}) {
  if (hasScheduleError) {
    return (
      <section className="today-v3__hero today-v3__hero--empty">
        <div className="today-v3__hero-main">
          <h1>Расписание временно недоступно</h1>
          <p>Не удалось загрузить ближайшие сессии с сервера. Откройте календарь или проверьте состояние бэка.</p>
          <div className="today-v3__hero-actions">
            <button type="button" className="today-v3__hero-btn is-primary" onClick={onOpenAppointments}>
              <IconCalendar size={16} />
              Открыть календарь
            </button>
            <button type="button" className="today-v3__hero-btn is-secondary" onClick={onOpenClients}>
              <IconUser size={15} />
              Открыть клиентов
            </button>
          </div>
        </div>
      </section>
    );
  }

  const minutesLeft = Math.max(0, Math.round(((parseDate(nextEvent?.startAt)?.getTime() || Date.now()) - Date.now()) / 60000));

  if (!hasSession) {
    return (
      <section className="today-v3__hero today-v3__hero--empty">
        <div className="today-v3__hero-main">
          <h1>Сегодня сессий нет</h1>
          <p>{isNew ? "Можно создать запись, добавить клиента или настроить расписание." : "На сегодня нет запланированных встреч. Можно создать запись или скопировать ссылку для клиента."}</p>
          <div className="today-v3__hero-actions">
            <button type="button" className="today-v3__hero-btn is-primary" onClick={onOpenAppointments}>
              <IconPlus size={16} />
              Создать запись
            </button>
            <button type="button" className="today-v3__hero-btn is-secondary" onClick={isNew ? onOpenClients : onCopyLink}>
              {isNew ? <IconUser size={15} /> : <IconLink size={15} />}
              {isNew ? "Добавить клиента" : "Скопировать ссылку"}
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="today-v3__hero today-v3__hero--session">
      <div className="today-v3__hero-main">
        <div className="today-v3__hero-eyebrow">
          <span />
          <small>Сессия скоро начнётся</small>
        </div>
        <h1>Сессия с {firstName(nextEvent?.title)} через {minutesLeft} минут</h1>
        <p>{nextEvent?.format || "Онлайн"} · {formatDate(nextEvent?.startAt, { day: "numeric", month: "long" })} · 50 минут</p>
        <div className="today-v3__hero-actions">
          <button type="button" className="today-v3__hero-btn is-primary" onClick={() => onOpenEvent(nextEvent)}>
            Начать сессию
          </button>
          <button type="button" className="today-v3__hero-btn is-secondary" onClick={() => onOpenNotes(nextEvent)}>
            <IconNote size={15} />
            Открыть заметку
          </button>
          <button type="button" className="today-v3__hero-btn is-secondary" onClick={() => onOpenClientCard(nextEvent)}>
            <IconCalendar size={15} />
            Карточка клиента
          </button>
        </div>
      </div>

      <aside className="today-v3__hero-side">
        <div className="today-v3__hero-side-label">Ближайшая сессия</div>
        <div className="today-v3__hero-side-time">{formatTime(nextEvent?.startAt)}</div>
        <div className="today-v3__hero-side-name">{nextEvent?.title}</div>
        <div className="today-v3__hero-side-copy">До начала: {minutesLeft} мин</div>
      </aside>
    </section>
  );
}

function ScheduleCard({ todayEvents, tomorrowSummary, hasScheduleError, onOpenAppointments, onOpenEvent, onOpenNotes, onOpenChat }) {
  return (
    <section className="today-v3__panel today-v3__schedule-card">
      <header className="today-v3__card-head">
        <div className="today-v3__card-head-left">
          <span className="today-v3__card-icon"><IconCalendar size={15} /></span>
          <span>Расписание</span>
        </div>
        <button type="button" className="today-v3__card-link" onClick={onOpenAppointments}>Открыть календарь →</button>
      </header>

      {hasScheduleError ? (
        <>
          <div className="today-v3__schedule-empty">
            <span>Не удалось загрузить записи на сегодня</span>
            <button type="button" className="today-v3__ghost-btn" onClick={onOpenAppointments}>
              <IconCalendar size={15} />
              Открыть календарь
            </button>
          </div>
          <div className="today-v3__schedule-foot">Проверьте доступность API бронирований и групповых сессий.</div>
        </>
      ) : !todayEvents.length ? (
        <>
          <div className="today-v3__schedule-empty">
            <span>На сегодня нет записей</span>
            <button type="button" className="today-v3__ghost-btn" onClick={onOpenAppointments}>
              <IconPlus size={15} />
              Создать запись
            </button>
          </div>
          <div className="today-v3__schedule-foot">{tomorrowSummary}</div>
        </>
      ) : (
        <>
          <div className="today-v3__schedule-list">
            {todayEvents.map((event, index) => (
              <div key={event.id} className={`today-v3__session-row ${index === 0 ? "is-nearest" : ""}`}>
                <div className="today-v3__session-time">
                  <strong>{formatTime(event.startAt)}</strong>
                  <span>{formatDate(event.startAt, { day: "numeric", month: "short" })}</span>
                </div>
                <div className="today-v3__session-avatar">{initials(event.title)}</div>
                <div className="today-v3__session-copy">
                  <strong>{event.title}</strong>
                  <span>{event.format}</span>
                </div>
                <div className="today-v3__session-actions">
                  <button type="button" onClick={() => onOpenEvent(event)}>▶</button>
                  <button type="button" onClick={() => onOpenNotes(event)}>✎</button>
                  <button type="button" onClick={onOpenChat}>💬</button>
                </div>
              </div>
            ))}
          </div>
          <div className="today-v3__schedule-foot">{tomorrowSummary}</div>
        </>
      )}
    </section>
  );
}

function PaymentsCard({ walletBalance, walletPending, weekIncome, latestPayout, hasPaymentsError, walletFeatureDisabled, onOpenPayments }) {
  if (walletFeatureDisabled) {
    return (
      <section className="today-v3__panel today-v3__payments-card">
        <header className="today-v3__card-head">
          <div className="today-v3__card-head-left">
            <span className="today-v3__card-icon"><IconWallet size={15} /></span>
            <span>Оплаты</span>
          </div>
          <button type="button" className="today-v3__card-link" onClick={onOpenPayments}>Открыть финансы →</button>
        </header>
        <div className="today-v3__schedule-empty">
          <span>Модуль финансов сейчас отключён</span>
          <button type="button" className="today-v3__ghost-btn" onClick={onOpenPayments}>Финансы</button>
        </div>
      </section>
    );
  }

  if (hasPaymentsError) {
    return (
      <section className="today-v3__panel today-v3__payments-card">
        <header className="today-v3__card-head">
          <div className="today-v3__card-head-left">
            <span className="today-v3__card-icon"><IconWallet size={15} /></span>
            <span>Оплаты</span>
          </div>
          <button type="button" className="today-v3__card-link" onClick={onOpenPayments}>Открыть финансы →</button>
        </header>
        <div className="today-v3__schedule-empty">
          <span>Не удалось загрузить данные по оплатам</span>
          <button type="button" className="today-v3__ghost-btn" onClick={onOpenPayments}>Открыть финансы</button>
        </div>
      </section>
    );
  }

  const hasPending = walletPending > 0 || String(latestPayout?.status || "").toUpperCase() === "PENDING";

  return (
    <section className="today-v3__panel today-v3__payments-card">
      <header className="today-v3__card-head">
        <div className="today-v3__card-head-left">
          <span className="today-v3__card-icon"><IconWallet size={15} /></span>
          <span>Оплаты</span>
        </div>
        <button type="button" className="today-v3__card-link" onClick={onOpenPayments}>Открыть финансы →</button>
      </header>

      <div className={`today-v3__payments-state ${hasPending ? "is-pending" : "is-good"}`}>
        <div className="today-v3__payments-state-icon">{hasPending ? "⏳" : "✓"}</div>
        <div className="today-v3__payments-state-copy">
          <strong>{hasPending ? "Есть средства в ожидании" : "Все оплаты обработаны"}</strong>
          <span>
            {hasPending
              ? `К выплате ${formatMoney(walletPending)}${latestPayout ? ` · ${payoutStatusLabel(latestPayout.status)}` : ""}`
              : "Новых проблем с оплатами сейчас нет."}
          </span>
        </div>
      </div>

      <div className="today-v3__payments-metrics">
        <div className="today-v3__payments-metric">
          <span>Доступно</span>
          <strong>{formatMoney(walletBalance)}</strong>
        </div>
        <div className="today-v3__payments-metric">
          <span>К выплате</span>
          <strong>{formatMoney(walletPending)}</strong>
        </div>
      </div>

      {latestPayout ? (
        <div className="today-v3__payments-payout">
          <div className="today-v3__payments-payout-copy">
            <strong>Последняя заявка</strong>
            <span>{latestPayout.requestedAt ? formatDate(latestPayout.requestedAt, { day: "numeric", month: "long" }) : "Дата недоступна"} · {payoutStatusLabel(latestPayout.status)}</span>
          </div>
          <div className="today-v3__payments-payout-amount">{formatMoney(latestPayout.amount)}</div>
        </div>
      ) : null}

      <div className="today-v3__payments-foot">
        <span>Доход за неделю: <strong>{formatMoney(weekIncome)}</strong></span>
        <button type="button" className="today-v3__ghost-btn" onClick={onOpenPayments}>Финансы</button>
      </div>
    </section>
  );
}

export default function StaffTodayDashboardV3() {
  const navigate = useNavigate();
  const toast = useToast();
  const { me, role, logout } = useAuth();
  const { unreadNotificationsCount = 0, messageUnreadTotal = 0 } = useNotifications();

  const [mobileSidebarOpen, setMobileSidebarOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [onboardingExpanded, setOnboardingExpanded] = React.useState(false);
  const [onboardingDismissed, setOnboardingDismissed] = React.useState(false);
  const [onboardingActionLoading, setOnboardingActionLoading] = React.useState(false);
  const [data, setData] = React.useState({
    profile: null,
    clientsTotal: 0,
    hasClientNotes: false,
    onboarding: buildOnboardingFallback({
      profile: null,
      clientsTotal: 0,
      hasClientNotes: false,
      hasPaymentsRecorded: false,
      hasPushNotifications: false,
      upcomingEvents: [],
    }),
    events: [],
    todayEvents: [],
    upcomingEvents: [],
    nextEvent: null,
    walletBalance: 0,
    walletPending: 0,
    payouts: [],
    errors: {},
  });

  const normalizedRole = normalizeRole(role || me?.role);
  const isPsychologistStaff = normalizedRole === "PSYCHOLOGIST" || normalizedRole === "PSYCHO";
  const userName = me?.name || me?.fullName || me?.login || me?.email || "коллега";
  const today = React.useMemo(() => new Date(), []);
  const historyFromKey = dayKey(addDays(today, -7));
  const todayKey = dayKey(today);
  const tomorrowKey = dayKey(addDays(today, 1));
  const greetingDate = React.useMemo(
    () => formatDate(today, { weekday: "long", day: "numeric", month: "long", year: "numeric" }),
    [today]
  );
  const userId = Number(me?.id) || null;
  const publicProfileUrl = userId && typeof window !== "undefined" ? `${window.location.origin}/psychologist/${userId}` : "";

  const toggleSidebarFromTopbar = React.useCallback(() => {
    if (typeof window !== "undefined" && window.matchMedia("(max-width: 860px)").matches) {
      setMobileSidebarOpen(true);
      return;
    }
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("burosoul:toggle-sidebar"));
    }
  }, []);

  const openSidebarEditorFromTopbar = React.useCallback(() => {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("burosoul:open-sidebar-editor"));
    }
  }, []);

  const handleLogout = React.useCallback(async () => {
    await logout();
    navigate("/login", { replace: true });
  }, [logout, navigate]);

  const load = React.useCallback(async () => {
    setLoading(true);
    const errors = {};

    const safe = async (key, fn, fallback) => {
      try {
        return await fn();
      } catch (error) {
        errors[key] = toErrorSnapshot(error);
        return fallback;
      }
    };

    const [profileRaw, clientsRaw, bookingsRaw, groupRaw, walletRaw, payoutsRaw, notificationsSettingsRaw, onboardingRaw] = await Promise.all([
      isPsychologistStaff ? safe("profile", () => profileApi.getMyPsychologistProfile(), null) : Promise.resolve(null),
      isPsychologistStaff ? safe("clients", () => request("/api/psychologist/clients"), []) : Promise.resolve([]),
      safe("bookings", () => (userId ? sessionsApi.listBookings(userId, { from: historyFromKey, to: tomorrowKey }) : []), []),
      safe("groups", () => sessionsApi.listMyGroupSessions({ page: 0, size: 20, from: historyFromKey, to: tomorrowKey }), []),
      safe("wallet", () => walletApi.getMyWallet(), {}),
      safe("payouts", () => walletApi.listPayouts({ page: 0, size: 3 }), { items: [] }),
      safe("notificationSettings", () => notificationsApi.getSettings(), null),
      safe("onboarding", () => onboardingApi.get(), null),
    ]);

    const profile = asObject(profileRaw);
    const clientItems = extractItems(clientsRaw);
    const bookingEvents = extractItems(bookingsRaw).map((item) => mapEvent(item, "INDIVIDUAL"));
    const groupEvents = extractItems(groupRaw).map((item) => mapEvent(item, "GROUP"));
    const events = mergeEvents(bookingEvents, groupEvents)
      .filter((item) => !CANCELED_STATUSES.has(String(item.status || "").toUpperCase()))
      .sort((a, b) => (parseDate(a.startAt)?.getTime() || 0) - (parseDate(b.startAt)?.getTime() || 0));
    const upcomingEvents = events
      .filter((item) => !TERMINAL_STATUSES.has(String(item.status || "").toUpperCase()))
      .sort((a, b) => (parseDate(a.startAt)?.getTime() || 0) - (parseDate(b.startAt)?.getTime() || 0));
    const todayEvents = upcomingEvents.filter((item) => dayKey(item.startAt) === todayKey);
    const nowTs = Date.now();
    const nextEvent = todayEvents.find((item) => (parseDate(item.startAt)?.getTime() || 0) >= nowTs) || upcomingEvents[0] || null;
    const wallet = asObject(walletRaw);
    const payouts = Array.isArray(payoutsRaw?.items) ? [...payoutsRaw.items] : [];
    const clientsTotal = errors.clients ? 0 : extractTotal(clientsRaw, clientItems, profile?.counts?.clientsCount || 0);
    const hasClientNotes = !errors.clients && clientItems.some((item) => Boolean(String(item?.note || item?.lastNote || "").trim()));
    const hasPaymentsRecorded = Number(wallet.totalEarned ?? wallet.netEarned ?? 0) > 0 || payouts.length > 0;
    const onboardingFallback = buildOnboardingFallback({
      profile,
      clientsTotal,
      hasClientNotes,
      hasPaymentsRecorded,
      hasPushNotifications: hasAnyEnabledNotificationSettings(notificationsSettingsRaw),
      upcomingEvents,
    });
    const onboarding = normalizeOnboardingPayload(onboardingRaw, onboardingFallback);
    const onboardingFlags = extractOnboardingFlags(onboardingRaw ?? onboardingFallback);

    setData({
      profile,
      clientsTotal,
      hasClientNotes,
      onboarding,
      events,
      todayEvents,
      upcomingEvents,
      nextEvent,
      walletBalance: Number(wallet.balance ?? wallet.availableBalance ?? 0) || 0,
      walletPending: Number(wallet.pendingAmount ?? 0) || 0,
      payouts: payouts.sort((a, b) => (parseDate(b?.requestedAt || b?.updatedAt)?.getTime() || 0) - (parseDate(a?.requestedAt || a?.updatedAt)?.getTime() || 0)),
      errors,
    });
    setOnboardingDismissed(onboardingFlags.dismissed);

    setLoading(false);
  }, [historyFromKey, isPsychologistStaff, todayKey, tomorrowKey, userId]);

  React.useEffect(() => {
    load();
  }, [load]);

  const scheduleUnavailable = Boolean(data.errors?.bookings || data.errors?.groups);
  const clientsUnavailable = Boolean(data.errors?.clients);
  const walletFeatureDisabled =
    hasDisabledMarker(data.errors?.wallet, "WALLET_DISABLED") ||
    hasDisabledMarker(data.errors?.payouts, "WALLET_DISABLED");
  const paymentsUnavailable = !walletFeatureDisabled && Boolean(data.errors?.wallet || data.errors?.payouts);
  const requiredDoneCount = Number(data.onboarding?.completedCount) || 0;
  const requiredTotalCount = Number(data.onboarding?.totalCount) || 0;
  const profileComplete = Boolean(data.onboarding?.steps?.find((step) => step.id === "profile_completed")?.completed);
  const workHoursConfigured = Boolean(data.onboarding?.steps?.find((step) => step.id === "working_hours_set")?.completed);
  const hasClients = Boolean(data.onboarding?.steps?.find((step) => step.id === "first_client_added")?.completed);
  const hasSessionCreated = Boolean(data.onboarding?.steps?.find((step) => step.id === "first_session_created")?.completed);
  const isNew = [profileComplete, workHoursConfigured, hasClients, hasSessionCreated].filter(Boolean).length < 4;

  const onboardingSteps = React.useMemo(
    () =>
      (Array.isArray(data.onboarding?.steps) ? data.onboarding.steps : []).map((step, index) => ({
        id: String(step.id || `step-${index + 1}`),
        number: index + 1,
        title: step.title || `Шаг ${index + 1}`,
        desc: step.description || "",
        cta: step.ctaLabel || "Перейти",
        optional: Boolean(step.optional),
        done: Boolean(step.completed),
        onClick: () => navigate(step.href || "/dashboard"),
      })),
    [data.onboarding?.steps, navigate]
  );

  const currentStep = onboardingSteps.find((step) => !step.done && !step.optional)
    || onboardingSteps.find((step) => !step.done)
    || onboardingSteps[onboardingSteps.length - 1];
  const totalDoneCount = onboardingSteps.filter((step) => step.done).length;
  const totalStepsCount = onboardingSteps.length;
  const onboardingProgressPercent = clampPercent(
    totalStepsCount ? (totalDoneCount / totalStepsCount) * 100 : 0
  );
  const tomorrowEvents = data.upcomingEvents.filter((item) => dayKey(item.startAt) === tomorrowKey);
  const showOnboarding = Boolean(data.onboarding?.shouldShow) && !onboardingDismissed && onboardingSteps.length > 0;

  const handleDismissOnboarding = React.useCallback(async () => {
    if (onboardingActionLoading) return;
    setOnboardingActionLoading(true);
    try {
      const response = await onboardingApi.dismiss();
      setData((prev) => ({
        ...prev,
        onboarding: normalizeOnboardingPayload(response, prev.onboarding),
      }));
      const flags = extractOnboardingFlags(response);
      setOnboardingDismissed(flags.dismissed || !flags.shouldShow);
    } catch (error) {
      toast.error(error?.message || "Не удалось скрыть подсказки");
    } finally {
      setOnboardingActionLoading(false);
    }
  }, [onboardingActionLoading, toast]);
  const upcomingTodayEvents = data.todayEvents.filter((item) => (parseDate(item.startAt)?.getTime() || 0) >= Date.now());
  const heroEvent = upcomingTodayEvents[0] || null;
  const latestPayout = data.payouts[0] || null;
  const weekAgoTs = Date.now() - 7 * 24 * 60 * 60 * 1000;
  const weekIncome = data.events.reduce((sum, item) => {
    const ts = parseDate(item.startAt)?.getTime() || 0;
    if (!ts || ts < weekAgoTs || ts > Date.now()) return sum;
    if (CANCELED_STATUSES.has(String(item.status || "").toUpperCase())) return sum;
    return sum + (Number(item.amount) || 0);
  }, 0);
  const tomorrowSummary = tomorrowEvents.length
    ? `Завтра: ${formatInt(tomorrowEvents.length)} сессии, первая в ${formatTime(tomorrowEvents[0]?.startAt)}`
    : "Завтра пока нет запланированных встреч";
  const unavailableSections = [
    data.errors?.profile ? "профиль" : "",
    clientsUnavailable ? "клиенты" : "",
    scheduleUnavailable ? "расписание" : "",
  ].filter(Boolean);
  const unavailableSectionsNotice = buildUnavailableSectionsNotice([
    data.errors?.profile ? "profile" : "",
    clientsUnavailable ? "clients" : "",
    scheduleUnavailable ? "schedule" : "",
  ]);

  const copyPublicLink = React.useCallback(async () => {
    if (!publicProfileUrl) {
      toast.error("Ссылка профиля пока недоступна");
      return;
    }
    try {
      if (!navigator?.clipboard?.writeText) throw new Error("clipboard");
      await navigator.clipboard.writeText(publicProfileUrl);
      toast.success("Ссылка скопирована");
    } catch {
      toast.error("Не удалось скопировать ссылку");
    }
  }, [publicProfileUrl, toast]);

  const openClientCard = React.useCallback((event) => {
    const clientId = String(event?.clientUserId || "").trim();
    if (!clientId) {
      navigate("/dashboard/clients");
      return;
    }
    navigate(`/dashboard/clients?clientId=${encodeURIComponent(clientId)}`);
  }, [navigate]);

  const openClientNotes = React.useCallback((event) => {
    const clientId = String(event?.clientUserId || "").trim();
    if (!clientId) {
      navigate("/dashboard/notes");
      return;
    }
    navigate(`/dashboard/notes?clientId=${encodeURIComponent(clientId)}`);
  }, [navigate]);

  const openEvent = React.useCallback(
    (event) => {
      if (!event) {
        navigate("/dashboard/calendar");
        return;
      }
      if (event.url) {
        if (!openExternalUrl(event.url)) toast.error("Некорректная ссылка на встречу");
        return;
      }
      if (event.kind === "GROUP" && event.id) {
        navigate(`/dashboard/group-sessions/${event.id}`);
        return;
      }
      navigate("/dashboard/calendar");
    },
    [navigate, toast]
  );

  return (
    <div className="today-v3 podcasts-hub">
      <div className="shell shell--shared podcasts-shell">
        <AppSidebarV2
          activeKey="dashboard"
          mobileOpen={mobileSidebarOpen}
          onCloseMobile={() => setMobileSidebarOpen(false)}
          userName={userName}
          roleLabel={rolePlanLabel(normalizedRole)}
          canOpenLiteratureEditor
          notificationsBadge={unreadNotificationsCount}
          chatBadge={messageUnreadTotal}
          onLogout={handleLogout}
        />

        <button
          className={`podcasts-overlay ${mobileSidebarOpen ? "is-open" : ""}`}
          type="button"
          onClick={() => setMobileSidebarOpen(false)}
          aria-label="Закрыть меню"
        />

        <main className="main podcasts-main cd-main today-v3__main">
          <div className="today-v3__topbar">
            <div className="today-v3__topbar-left">
              <button type="button" className="today-v3__burger" onClick={toggleSidebarFromTopbar} aria-label="Свернуть или открыть меню" title="Свернуть или открыть меню">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </svg>
              </button>
              <button type="button" className="today-v3__sidebar-settings" onClick={openSidebarEditorFromTopbar} aria-label="Настроить меню" title="Настроить меню">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 6h10" />
                  <path d="M18 6h2" />
                  <path d="M4 12h2" />
                  <path d="M10 12h10" />
                  <path d="M4 18h8" />
                  <path d="M16 18h4" />
                  <circle cx="16" cy="6" r="2" />
                  <circle cx="8" cy="12" r="2" />
                  <circle cx="14" cy="18" r="2" />
                </svg>
              </button>
              <div>
                <div className="today-v3__topbar-title">Сегодня</div>
                <div className="today-v3__topbar-date">{greetingDate}</div>
              </div>
            </div>

            <div className="today-v3__topbar-right">
              <button type="button" className="today-v3__notif-btn" aria-label="Уведомления" title="Уведомления" onClick={() => navigate("/notifications")}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
                {unreadNotificationsCount > 0 ? <span className="today-v3__notif-dot" /> : null}
              </button>
              <button type="button" className="today-v3__tb-btn is-ghost" onClick={copyPublicLink}>
                <IconLink size={14} />
                <span>Скопировать ссылку</span>
              </button>
              <button type="button" className="today-v3__tb-btn is-solid" onClick={() => navigate("/dashboard/calendar")}>
                <IconPlus size={14} />
                <span>Создать запись</span>
              </button>
            </div>
          </div>

          <div className="today-v3__content">
            <div className="today-v3__layout">
              <div className="today-v3__main-column">
                {unavailableSections.length ? (
                  <section className="today-v3__panel today-v3__load-warning">
                    <strong>{unavailableSectionsNotice.title || "Часть данных недоступна"}</strong>
                    <p>{unavailableSectionsNotice.message || `Не удалось загрузить: ${unavailableSections.join(", ")}.`}</p>
                  </section>
                ) : null}

                {showOnboarding ? (
                  <OnboardingCard
                    dismissed={onboardingDismissed}
                    expanded={onboardingExpanded}
                    doneCount={totalDoneCount}
                    requiredDoneCount={requiredDoneCount}
                    requiredTotalCount={requiredTotalCount}
                    totalCount={totalStepsCount}
                    progressPercent={onboardingProgressPercent}
                    steps={onboardingSteps}
                    currentStep={currentStep}
                    dismissing={onboardingActionLoading}
                    onDismiss={handleDismissOnboarding}
                    onToggleExpanded={() => setOnboardingExpanded((value) => !value)}
                  />
                ) : null}

                <HeroCard
                  hasSession={Boolean(heroEvent)}
                  nextEvent={heroEvent}
                  isNew={isNew}
                  hasScheduleError={scheduleUnavailable}
                  onOpenAppointments={() => navigate("/dashboard/calendar")}
                  onOpenClients={() => navigate("/dashboard/clients")}
                  onCopyLink={copyPublicLink}
                  onOpenClientCard={openClientCard}
                  onOpenNotes={openClientNotes}
                  onOpenEvent={openEvent}
                />

                <ScheduleCard
                  todayEvents={data.todayEvents}
                  tomorrowSummary={loading ? "Загружаем расписание…" : tomorrowSummary}
                  hasScheduleError={scheduleUnavailable}
                  onOpenAppointments={() => navigate("/dashboard/calendar")}
                  onOpenEvent={openEvent}
                  onOpenNotes={openClientNotes}
                  onOpenChat={() => navigate("/chat")}
                />

                <PaymentsCard
                  walletBalance={data.walletBalance}
                  walletPending={data.walletPending}
                  weekIncome={weekIncome}
                  latestPayout={latestPayout}
                  hasPaymentsError={paymentsUnavailable}
                  walletFeatureDisabled={walletFeatureDisabled}
                  onOpenPayments={() => navigate("/dashboard/settings?tab=payments")}
                />

                <div className="today-v3__quick-mobile">
                  <QuickActionsCard
                    isNew={isNew}
                    onOpenAppointments={() => navigate("/dashboard/calendar")}
                    onOpenClients={() => navigate("/dashboard/clients")}
                    onCopyLink={copyPublicLink}
                    onOpenCalendar={() => navigate("/dashboard/calendar")}
                  />
                </div>
              </div>

              <aside className="today-v3__side-rail">
                <QuickActionsCard
                  isNew={isNew}
                  onOpenAppointments={() => navigate("/dashboard/calendar")}
                  onOpenClients={() => navigate("/dashboard/clients")}
                  onCopyLink={copyPublicLink}
                  onOpenCalendar={() => navigate("/dashboard/calendar")}
                />
              </aside>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
